import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { FieldTree, form, min, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { merge, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { Lasku } from '@lemon/core-firebase/model/lasku'
import {
  KuukaudenJarjestysnumero,
  KuukaudenLahetysTyyppi,
  KuukausittainenLahetysPaiva,
  LAHETYSMAARA_MAX,
  LAHETYSMAARA_MIN,
  LaskuLahetyksenToistoasetukset,
  LaskuToistoTyyppi,
  ToistonPaattyminen,
  ToistonPaattymisTyyppi,
  Viikonpaiva,
  VuosittainenLahetysPaiva,
} from '@lemon/core-firebase/model/lasku-toistuva'
import { LaskuLahetyksenToistoService } from '@lemon/core-firebase/service/lasku/lasku-lahetyksen-toisto.service'
import { DateService } from '@lemon/core/service/date.service'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'

export interface LaskuAjastaDialogData {
  lasku: Lasku
  /** Jos muokataan olemassa olevia asetuksia */
  nykyisetAsetukset?: LaskuLahetyksenToistoasetukset
}

interface ToistoValinta {
  arvo: LaskuToistoTyyppi
  nimi: string
}

interface PaattyminenValinta {
  arvo: ToistonPaattymisTyyppi
  nimi: string
}

interface LaskuAjastaArvot {
  ensimmainenPvm: Date
  kuukaudenPaiva: number
  kuukaudenJarjestysnumero: KuukaudenJarjestysnumero
  kuukaudenViikonpaiva: Viikonpaiva
  kuukaudenLahetysTyyppi: KuukaudenLahetysTyyppi
  vuosiKuukausi: number
  vuosiPaiva: number
  toistoTyyppi: LaskuToistoTyyppi
  paattyminenTyyppi: ToistonPaattymisTyyppi
  paattymispvm: Date | null
  lahetysmaara: number | null
}

interface LaskuAjastaLomake {
  ensimmainenLahetyspvm: Date | null
  toistoTyyppi: LaskuToistoTyyppi
  kuukaudenLahetysTyyppi: KuukaudenLahetysTyyppi
  kuukaudenPaiva: number
  kuukaudenJarjestysnumero: KuukaudenJarjestysnumero
  kuukaudenViikonpaiva: Viikonpaiva
  vuosiKuukausi: number
  vuosiPaiva: number
  paattyminenTyyppi: ToistonPaattymisTyyppi
  paattymispvm: Date | null
  lahetysmaara: number | null
}

@Component({
  templateUrl: './lasku-ajasta.dialog.html',
  styleUrls: ['./lasku-ajasta.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskuAjastaDialog implements OnInit, OnDestroy {
  lomake: FieldTree<LaskuAjastaLomake>
  validointiVirheet: string[] = []

  toistoValinnat: ToistoValinta[] = []
  viikonpaivat: { arvo: Viikonpaiva; nimi: string }[] = []
  jarjestysnumerot: { arvo: KuukaudenJarjestysnumero; nimi: string }[] = []
  paattyminenValinnat: PaattyminenValinta[] = []

  kuukaudenPaivat: number[] = Array.from({ length: 31 }, (_, i) => i + 1)
  kuukaudet: { arvo: number; nimi: string }[] = []
  minDate: Date | null = null

  private _sallitutPaattymispvmt = new Set<number>()
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LaskuAjastaDialogData,
    private _dialogRef: MatDialogRef<LaskuAjastaDialog>,
    private _dateService: DateService,
    private _translationService: LemonTranslationService,
    private _toistoService: LaskuLahetyksenToistoService,
    private _formValidationService: FormValidationService,
  ) {}

  ngOnInit() {
    this.minDate = this._dateService.lisaaPaivia(new Date(), 1)

    this.toistoValinnat = this._luoToistoValinnat()
    this.viikonpaivat = this._luoViikonpaivat()
    this.jarjestysnumerot = this._luoJarjestysnumerot()
    this.paattyminenValinnat = this._luoPaattyminenValinnat()
    this.kuukaudet = this._luoKuukaudet()

    const lomakeArvot = this._alustaLomakeArvot()
    this.lomake = this._luoLomake(lomakeArvot)
    this._asetaLomakeListenerit()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  get naytaKuukaudenValinta(): boolean {
    const tyyppi = this.lomake.toistoTyyppi().value()
    return tyyppi === LaskuToistoTyyppi.KUUKAUSI || tyyppi === LaskuToistoTyyppi.KUUKAUSI_3 || tyyppi === LaskuToistoTyyppi.KUUKAUSI_6
  }

  get naytaVuodenValinta(): boolean {
    return this.lomake.toistoTyyppi().value() === LaskuToistoTyyppi.VUOSI
  }

  get naytaPaattyminenValinta(): boolean {
    return this.lomake.toistoTyyppi().value() !== LaskuToistoTyyppi.KERRAN
  }

  get naytaKuukaudenPaivaVaroitus(): boolean {
    if (!this.naytaKuukaudenValinta) {
      return false
    }
    if (this.lomake.kuukaudenLahetysTyyppi().value() !== KuukaudenLahetysTyyppi.PAIVAMAARA) {
      return false
    }
    const paiva = this.lomake.kuukaudenPaiva().value()
    return paiva >= 29
  }

  get toistonKuvaus(): string {
    if (!this.lomake) {
      return ''
    }

    const tyyppi = this.lomake.toistoTyyppi().value()
    if (!tyyppi) {
      return ''
    }

    if (tyyppi === LaskuToistoTyyppi.KERRAN) {
      return this._muodostaKertaLaskunKuvaus()
    }

    return this._muodostaToistuvanLaskunKuvaus(tyyppi)
  }

  paattymispvmFilter = (date: Date | null): boolean => {
    if (!date) {
      return false
    }
    const numberDate = this._dateService.dateToNumber(date)
    return this._sallitutPaattymispvmt.has(numberDate)
  }

  peruuta() {
    this._dialogRef.close(null)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private _luoLomake(arvot: LaskuAjastaArvot): FieldTree<LaskuAjastaLomake> {
    return form(
      signal<LaskuAjastaLomake>({
        ensimmainenLahetyspvm: arvot.ensimmainenPvm,
        toistoTyyppi: arvot.toistoTyyppi,
        kuukaudenLahetysTyyppi: arvot.kuukaudenLahetysTyyppi,
        kuukaudenPaiva: arvot.kuukaudenPaiva,
        kuukaudenJarjestysnumero: arvot.kuukaudenJarjestysnumero,
        kuukaudenViikonpaiva: arvot.kuukaudenViikonpaiva,
        vuosiKuukausi: arvot.vuosiKuukausi,
        vuosiPaiva: arvot.vuosiPaiva,
        paattyminenTyyppi: arvot.paattyminenTyyppi,
        paattymispvm: arvot.paattymispvm,
        lahetysmaara: arvot.lahetysmaara,
      }),
      polku => {
        required(polku.ensimmainenLahetyspvm)
        required(polku.toistoTyyppi)
        required(polku.paattyminenTyyppi)
        required(polku.paattymispvm, { when: ({ valueOf }) => valueOf(polku.paattyminenTyyppi) === ToistonPaattymisTyyppi.PAATTYMISPVM })
        const lahetysmaaraKaytossa = { when: ({ valueOf }) => valueOf(polku.paattyminenTyyppi) === ToistonPaattymisTyyppi.LAHETYSMAARA }
        required(polku.lahetysmaara, lahetysmaaraKaytossa)
        min(polku.lahetysmaara, LAHETYSMAARA_MIN, lahetysmaaraKaytossa)
        // Signal Formsin max() lisäisi kenttään natiivin max-attribuutin, jota templatessa ei ollut
        validoiReaktiivisesti(polku.lahetysmaara, Validators.max(LAHETYSMAARA_MAX), lahetysmaaraKaytossa)
      },
      { injector: this._injector },
    )
  }

  private _alustaLomakeArvot(): LaskuAjastaArvot {
    const huominen = this._dateService.lisaaPaivia(new Date(), 1)
    const huominenLocal = this._dateService.dateToLocalDate(huominen)

    const oletusarvot: LaskuAjastaArvot = {
      ensimmainenPvm: huominen,
      kuukaudenPaiva: huominenLocal.day,
      kuukaudenJarjestysnumero: KuukaudenJarjestysnumero.ENSIMMAINEN,
      kuukaudenViikonpaiva: huominen.getDay() || 7,
      kuukaudenLahetysTyyppi: KuukaudenLahetysTyyppi.PAIVAMAARA,
      vuosiKuukausi: huominenLocal.month,
      vuosiPaiva: huominenLocal.day,
      toistoTyyppi: LaskuToistoTyyppi.KERRAN,
      paattyminenTyyppi: ToistonPaattymisTyyppi.EI_PAATTYMISTA,
      paattymispvm: null,
      lahetysmaara: null,
    }

    const nykyiset = this.data.nykyisetAsetukset
    if (nykyiset) {
      this._lataaOlemassaOlevatAsetukset(oletusarvot, nykyiset)
    }
    return oletusarvot
  }

  private _lataaOlemassaOlevatAsetukset(arvot: LaskuAjastaArvot, nykyiset: LaskuLahetyksenToistoasetukset): void {
    arvot.ensimmainenPvm = this._dateService.numberToDate(nykyiset.ensimmainenLahetyspvm)
    arvot.toistoTyyppi = nykyiset.toistoTyyppi
    arvot.paattyminenTyyppi = nykyiset.paattyminen.tyyppi

    if (nykyiset.lahetyspaiva) {
      if (nykyiset.toistoTyyppi === LaskuToistoTyyppi.KUUKAUSI || nykyiset.toistoTyyppi === LaskuToistoTyyppi.KUUKAUSI_3 || nykyiset.toistoTyyppi === LaskuToistoTyyppi.KUUKAUSI_6) {
        const lahetyspaiva = nykyiset.lahetyspaiva as KuukausittainenLahetysPaiva
        arvot.kuukaudenLahetysTyyppi = lahetyspaiva.tyyppi

        if (lahetyspaiva.tyyppi === KuukaudenLahetysTyyppi.PAIVAMAARA) {
          arvot.kuukaudenPaiva = lahetyspaiva.paiva
        } else {
          arvot.kuukaudenJarjestysnumero = lahetyspaiva.jarjestysnumero
          arvot.kuukaudenViikonpaiva = lahetyspaiva.viikonpaiva
        }
      } else if (nykyiset.toistoTyyppi === LaskuToistoTyyppi.VUOSI) {
        const lahetyspaiva = nykyiset.lahetyspaiva as VuosittainenLahetysPaiva
        arvot.vuosiKuukausi = lahetyspaiva.kuukausi
        arvot.vuosiPaiva = lahetyspaiva.paiva
      }
    }
    const paattyminen = nykyiset.paattyminen
    if (paattyminen.tyyppi === ToistonPaattymisTyyppi.PAATTYMISPVM) {
      arvot.paattymispvm = this._dateService.numberToDate(paattyminen.paattymispvm)
    } else if (paattyminen.tyyppi === ToistonPaattymisTyyppi.LAHETYSMAARA) {
      arvot.lahetysmaara = paattyminen.lahetysmaara
    }
  }

  private _asetaLomakeListenerit() {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake.ensimmainenLahetyspvm)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(pvm => {
        if (pvm) {
          const pvmLocal = this._dateService.dateToLocalDate(pvm)
          asetaArvo(this.lomake.kuukaudenPaiva, pvmLocal.day, { ilmoita: false })
          asetaArvo(this.lomake.kuukaudenViikonpaiva, pvm.getDay() || 7, { ilmoita: false })
          asetaArvo(this.lomake.vuosiKuukausi, pvmLocal.month, { ilmoita: false })
          asetaArvo(this.lomake.vuosiPaiva, pvmLocal.day, { ilmoita: false })
        }
        this._laskeSallitutPaattymispvmt()
      })

    merge(
      muutokset(this.lomake.toistoTyyppi),
      muutokset(this.lomake.kuukaudenLahetysTyyppi),
      muutokset(this.lomake.kuukaudenPaiva),
      muutokset(this.lomake.kuukaudenJarjestysnumero),
      muutokset(this.lomake.kuukaudenViikonpaiva),
      muutokset(this.lomake.vuosiKuukausi),
      muutokset(this.lomake.vuosiPaiva),
    )
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this._laskeSallitutPaattymispvmt())

    this._laskeSallitutPaattymispvmt()
  }

  tallenna() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const formValue = this.lomake().value()
    const toistoTyyppi = formValue.toistoTyyppi
    const ensimmainenPvm = this._dateService.dateToNumber(formValue.ensimmainenLahetyspvm)

    const lahetyspaiva = this._muodostaLahetyspaiva()
    const paattyminen = this._muodostaPaattyminen(toistoTyyppi)

    const asetukset: LaskuLahetyksenToistoasetukset = {
      ensimmainenLahetyspvm: ensimmainenPvm,
      toistoTyyppi,
      paattyminen,
    }

    // Lähetyspäivä tarvitaan vain toistuvissa lähetyksissä
    if (lahetyspaiva) {
      asetukset.lahetyspaiva = lahetyspaiva
    }

    const virheet = this._toistoService.numberDateValidoi(asetukset)
    if (virheet.length > 0) {
      this.validointiVirheet = virheet.map(v => this._translationService.lokalisoi(v.viesti))
      return
    }

    this.validointiVirheet = []
    this._dialogRef.close(asetukset)
  }

  private _muodostaPaattyminen(toistoTyyppi: LaskuToistoTyyppi): ToistonPaattyminen {
    if (toistoTyyppi === LaskuToistoTyyppi.KERRAN) {
      return { tyyppi: ToistonPaattymisTyyppi.KERRAN }
    }
    const paattyminenTyyppi = this.lomake.paattyminenTyyppi().value()
    if (paattyminenTyyppi === ToistonPaattymisTyyppi.PAATTYMISPVM) {
      return {
        tyyppi: ToistonPaattymisTyyppi.PAATTYMISPVM,
        paattymispvm: this._dateService.dateToNumber(this.lomake.paattymispvm().value()),
      }
    }
    if (paattyminenTyyppi === ToistonPaattymisTyyppi.LAHETYSMAARA) {
      return {
        tyyppi: ToistonPaattymisTyyppi.LAHETYSMAARA,
        lahetysmaara: this.lomake.lahetysmaara().value(),
      }
    }
    return { tyyppi: ToistonPaattymisTyyppi.EI_PAATTYMISTA }
  }

  private _laskeSallitutPaattymispvmt(): void {
    const ensimmainenPvm = this.lomake.ensimmainenLahetyspvm().value()
    const toistoTyyppi = this.lomake.toistoTyyppi().value()

    if (!ensimmainenPvm || !toistoTyyppi) {
      this._sallitutPaattymispvmt.clear()
      return
    }

    const lahetyspaiva = this._muodostaLahetyspaiva()
    const sallitut = this._toistoService.annaSallitutPaattymispaivamaarat(this._dateService.dateToNumber(ensimmainenPvm), toistoTyyppi, lahetyspaiva)

    this._sallitutPaattymispvmt = new Set(sallitut)
  }

  private _muodostaLahetyspaiva(): KuukausittainenLahetysPaiva | VuosittainenLahetysPaiva | undefined {
    if (this.naytaKuukaudenValinta) {
      const lahetysTyyppi = this.lomake.kuukaudenLahetysTyyppi().value()
      if (lahetysTyyppi === KuukaudenLahetysTyyppi.PAIVAMAARA) {
        return {
          tyyppi: KuukaudenLahetysTyyppi.PAIVAMAARA,
          paiva: this.lomake.kuukaudenPaiva().value(),
        }
      } else {
        return {
          tyyppi: KuukaudenLahetysTyyppi.PAIVA,
          jarjestysnumero: this.lomake.kuukaudenJarjestysnumero().value(),
          viikonpaiva: this.lomake.kuukaudenViikonpaiva().value(),
        }
      }
    } else if (this.naytaVuodenValinta) {
      return {
        kuukausi: this.lomake.vuosiKuukausi().value(),
        paiva: this.lomake.vuosiPaiva().value(),
      }
    }
    return undefined
  }

  private _muodostaToistotiheysKuvaus(tyyppi: LaskuToistoTyyppi): string {
    if (tyyppi === LaskuToistoTyyppi.KUUKAUSI) {
      return this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.joka-kuukausi')
    } else if (tyyppi.startsWith('kk')) {
      const maara = tyyppi.substring(2)
      return this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.joka-n-kuukausi', { maara })
    } else if (tyyppi === LaskuToistoTyyppi.VUOSI) {
      return this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.joka-vuosi')
    }
    return ''
  }

  private _muodostaKertaLaskunKuvaus(): string {
    const ensimmainenPvm = this.lomake.ensimmainenLahetyspvm().value()
    const pvm = this._muotoilePvm(ensimmainenPvm)
    return this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.kerran-pvm', { pvm })
  }

  private _muodostaToistuvanLaskunKuvaus(tyyppi: LaskuToistoTyyppi): string {
    const ensimmainenPvm = this.lomake.ensimmainenLahetyspvm().value()
    const ensimmainenLause = this._muodostaEnsimmaisenLaskunKuvaus(ensimmainenPvm)
    const toinenLause = this._muodostaToistonJatkoKuvaus(ensimmainenPvm, tyyppi)
    return ensimmainenLause + ' ' + toinenLause
  }

  private _muodostaEnsimmaisenLaskunKuvaus(ensimmainenPvm: Date): string {
    const pvmStr = this._muotoilePvm(ensimmainenPvm)
    return this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.ensimmainen-lasku', { pvm: pvmStr })
  }

  private _muodostaToistonJatkoKuvaus(ensimmainenPvm: Date, tyyppi: LaskuToistoTyyppi): string {
    const toistotiheys = this._muodostaToistotiheysKuvaus(tyyppi)
    const paivaKuvaus = this._muodostaPaivaKuvaus()

    let kuvaus = this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.taman-jalkeen', { toistotiheys, paiva: paivaKuvaus })

    const seuraavaPvm = this._laskeSeuraavaPvm(ensimmainenPvm, tyyppi)
    if (seuraavaPvm) {
      const ensimmainenPvmStr = this._muotoilePvm(ensimmainenPvm)
      const seuraavaPvmStr = this._muotoilePvm(seuraavaPvm)

      if (seuraavaPvmStr !== ensimmainenPvmStr) {
        kuvaus += ' ' + this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.seuraava-lasku', { pvm: seuraavaPvmStr })
      }
    }

    return kuvaus
  }

  private _muodostaPaivaKuvaus(): string {
    if (this.naytaKuukaudenValinta) {
      const lahetysTyyppi = this.lomake.kuukaudenLahetysTyyppi().value()
      if (lahetysTyyppi === KuukaudenLahetysTyyppi.PAIVAMAARA) {
        const paiva = this.lomake.kuukaudenPaiva().value()
        return this._translationService.lokalisoi('lasku.ajasta-dialog.kuvaus.paivana', { paiva: paiva + '.' })
      } else {
        const jarj = this.lomake.kuukaudenJarjestysnumero().value()
        const vp = this.lomake.kuukaudenViikonpaiva().value()
        const jarjestysnumero = this.jarjestysnumerot.find(j => j.arvo === jarj)
        const viikonpaiva = this.viikonpaivat.find(v => v.arvo === vp)
        if (jarjestysnumero && viikonpaiva) {
          return jarjestysnumero.nimi.toLowerCase() + ' ' + viikonpaiva.nimi.toLowerCase()
        }
      }
    } else if (this.naytaVuodenValinta) {
      const kk = this.lomake.vuosiKuukausi().value()
      const pv = this.lomake.vuosiPaiva().value()
      return pv + '.' + kk + '.'
    }
    return ''
  }

  private _luoToistoValinnat(): ToistoValinta[] {
    return [
      { arvo: LaskuToistoTyyppi.KERRAN, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.toisto-valinnat.kerran') },
      { arvo: LaskuToistoTyyppi.KUUKAUSI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.toisto-valinnat.kuukausi') },
      { arvo: LaskuToistoTyyppi.KUUKAUSI_3, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.toisto-valinnat.kuukausi-3') },
      { arvo: LaskuToistoTyyppi.KUUKAUSI_6, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.toisto-valinnat.kuukausi-6') },
      { arvo: LaskuToistoTyyppi.VUOSI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.toisto-valinnat.vuosi') },
    ]
  }

  private _luoViikonpaivat(): { arvo: Viikonpaiva; nimi: string }[] {
    return [
      { arvo: Viikonpaiva.MAANANTAI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.maanantai') },
      { arvo: Viikonpaiva.TIISTAI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.tiistai') },
      { arvo: Viikonpaiva.KESKIVIIKKO, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.keskiviikko') },
      { arvo: Viikonpaiva.TORSTAI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.torstai') },
      { arvo: Viikonpaiva.PERJANTAI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.perjantai') },
      { arvo: Viikonpaiva.LAUANTAI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.lauantai') },
      { arvo: Viikonpaiva.SUNNUNTAI, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.viikonpaivat.sunnuntai') },
    ]
  }

  private _luoJarjestysnumerot(): { arvo: KuukaudenJarjestysnumero; nimi: string }[] {
    return [
      { arvo: KuukaudenJarjestysnumero.ENSIMMAINEN, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.jarjestysnumerot.ensimmainen') },
      { arvo: KuukaudenJarjestysnumero.TOINEN, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.jarjestysnumerot.toinen') },
      { arvo: KuukaudenJarjestysnumero.KOLMAS, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.jarjestysnumerot.kolmas') },
      { arvo: KuukaudenJarjestysnumero.NELJAS, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.jarjestysnumerot.neljas') },
      { arvo: KuukaudenJarjestysnumero.VIIMEINEN, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.jarjestysnumerot.viimeinen') },
    ]
  }

  private _luoPaattyminenValinnat(): PaattyminenValinta[] {
    return [
      { arvo: ToistonPaattymisTyyppi.EI_PAATTYMISTA, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.paattyminen-valinnat.ei-paattymista') },
      { arvo: ToistonPaattymisTyyppi.PAATTYMISPVM, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.paattyminen-valinnat.paattymispvm') },
      { arvo: ToistonPaattymisTyyppi.LAHETYSMAARA, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.paattyminen-valinnat.lahetysmaara') },
    ]
  }

  private _luoKuukaudet(): { arvo: number; nimi: string }[] {
    return [
      { arvo: 1, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.1') },
      { arvo: 2, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.2') },
      { arvo: 3, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.3') },
      { arvo: 4, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.4') },
      { arvo: 5, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.5') },
      { arvo: 6, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.6') },
      { arvo: 7, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.7') },
      { arvo: 8, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.8') },
      { arvo: 9, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.9') },
      { arvo: 10, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.10') },
      { arvo: 11, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.11') },
      { arvo: 12, nimi: this._translationService.lokalisoi('lasku.ajasta-dialog.kuukaudet.12') },
    ]
  }

  private _muotoilePvm(date: Date): string {
    const local = this._dateService.dateToLocalDate(date)
    return `${local.day.toString().padStart(2, '0')}.${local.month.toString().padStart(2, '0')}.${local.year}`
  }

  private _laskeSeuraavaPvm(ensimmainenPvm: Date, tyyppi: LaskuToistoTyyppi): Date | null {
    try {
      const lahetyspaiva = this._muodostaLahetyspaiva()
      const asetukset: LaskuLahetyksenToistoasetukset = {
        ensimmainenLahetyspvm: this._dateService.dateToNumber(ensimmainenPvm),
        toistoTyyppi: tyyppi,
        lahetyspaiva,
        paattyminen: { tyyppi: ToistonPaattymisTyyppi.EI_PAATTYMISTA },
      }

      const seuraavaPvmNumber = this._toistoService.annaSeuraavaLahetysPvm(asetukset)
      return seuraavaPvmNumber ? this._dateService.numberToDate(seuraavaPvmNumber) : null
    } catch (e) {
      return null
    }
  }
}
